import type { ComponentType, ReactNode } from "react";
import { IconInbox, type IconProps } from "@tabler/icons-react";
import { Heading, Text } from "./typography";
import { Button } from "./button";
import emptyDefaultSvg from "../../assets/images/empty_state.svg";
import emptyAddSvg from "../../assets/images/empty_state_add.svg";

/**
 * Generic empty-state illustration. Two backgrounds:
 *
 * - **default** (`empty_state.svg`) — for read-only empty surfaces where the
 *   user can't directly add items (orders, notifications, history pages).
 * - **add** (`empty_state_add.svg`) — for surfaces where the user can take
 *   action (no products yet → add one, no liked items → browse, etc.).
 *
 * The variant is chosen automatically based on whether `primaryAction` is
 * provided. Override via the explicit `actionable` prop when needed.
 *
 * The Tabler icon passed via `icon` is rendered on top of the background's
 * center card (the white rounded square at viewBox center) and rendered in
 * Accent/Cyan/700 to match the cyan-tinted illustration.
 *
 * **Sizing.** The illustration is responsive: it uses `clamp(150px, 28%, 240px)`
 * (or `clamp(120px, 22%, 180px)` for `size="sm"`) so the card scales with
 * its parent's width but never gets oversized or microscopic. The icon
 * inside is 38% of the illustration width so it always sits within the
 * white card. Centering is handled by `flex flex-col items-center` on the
 * outer wrapper — the parent should typically wrap this in a flex container
 * with `justify-center` for full-width horizontal centering.
 */

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  leftIcon?: ReactNode;
}

export interface EmptyStateProps {
  /**
   * Tabler icon component (e.g. `IconBell`). Rendered centered over the bg.
   * Defaults to a generic inbox icon when omitted.
   */
  icon?: ComponentType<IconProps>;
  title: string;
  description?: string;
  size?: "sm" | "md";
  /** Primary CTA. Omit for read-only/buyer states with no actions. */
  primaryAction?: EmptyStateAction;
  /** Secondary CTA. Always paired with `primaryAction`. */
  secondaryAction?: EmptyStateAction;
  /**
   * Forces the "add" or "default" background. Defaults to `true` when a
   * `primaryAction` is provided, `false` otherwise.
   */
  actionable?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon = IconInbox,
  title,
  description,
  size = "md",
  primaryAction,
  secondaryAction,
  actionable,
  className = "",
}: EmptyStateProps) {
  const isActionable = actionable ?? !!primaryAction;
  const bgSrc = isActionable ? emptyAddSvg : emptyDefaultSvg;

  const hasActions = !!(primaryAction || secondaryAction);
  const isCompact = size === "sm";

  // Responsive illustration: scales between min/max with the parent. We keep
  // the SVG's 220:180 aspect ratio via aspect-ratio so it never distorts.
  const illustrationStyle = isCompact
    ? { width: "clamp(120px, 22%, 180px)", aspectRatio: "220 / 180" }
    : { width: "clamp(150px, 28%, 240px)", aspectRatio: "220 / 180" };

  return (
    <div
      className={`flex w-full flex-col items-center text-center ${
        isCompact ? "gap-4 px-4 py-6" : "gap-6 px-6 py-10"
      } ${className}`}
    >
      <div className="relative" style={illustrationStyle}>
        <img
          src={bgSrc}
          alt=""
          aria-hidden
          draggable="false"
          className="block h-full w-full select-none"
        />
        {/*
          The white card sits at viewBox (62..158, 42..138) — center (110, 90),
          which is the geometric center of the 220x180 SVG. The icon is 38% of
          the box width so it always fits within the card.
        */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Icon className="h-[38%] w-[38%] text-accentCyan700" />
        </div>
      </div>

      <div
        className={`flex max-w-md flex-col items-center text-center ${
          isCompact ? "gap-1.5" : "gap-2"
        }`}
      >
        <Heading variant="h3" tone="dark">
          {title}
        </Heading>
        {description && (
          <Text variant="body" tone="muted">
            {description}
          </Text>
        )}
      </div>

      {hasActions && (
        <div
          className={`flex flex-wrap items-center justify-center ${
            isCompact ? "gap-2" : "gap-3"
          }`}
        >
          {primaryAction && (
            <Button
              variant="primary"
              leftIcon={primaryAction.leftIcon}
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="secondary"
              leftIcon={secondaryAction.leftIcon}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
